import { useMemo, useState } from 'react';
import { ProjectDashboard, UserMode } from './components/ProjectDashboard';
import { SceneEditor } from './components/SceneEditor';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { PortfolioViewer } from './components/PortfolioViewer';
import { StudentRoleDashboard } from './components/StudentRoleDashboard';
import { RehearsalVideoChat } from './components/RehearsalVideoChat';
import { PerformanceProject, Scene, User } from './types/models';
import './styles/responsive.css';
import './styles/accessibility.css';

type ActiveView = 'dashboard' | 'portfolio' | 'roles' | 'analytics' | 'rehearsal';

function App() {
  const [userMode, setUserMode] = useState<UserMode>('student');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ActiveView>('dashboard');

  // Mock data - in real app, this would come from API/state management
  const mockUser: User = useMemo(
    () => ({
      id: 'user-1',
      email: 'student@example.com',
      role: 'student',
      profile: {
        firstName: 'John',
        lastName: 'Doe',
        disciplines: ['music', 'drama'],
      },
      preferences: {
        defaultMode: 'student',
        theme: 'light',
      },
      createdAt: new Date(),
    }),
    []
  );

  const mockProjects: PerformanceProject[] = useMemo(
    () => [
      {
        id: 'project-1',
        title: 'Spring Showcase',
        ownerId: mockUser.id,
        visibility: 'class',
        scenes: [
          {
            id: 'scene-1',
            projectId: 'project-1',
            title: 'Opening Number',
            sequenceOrder: 0,
            layers: [
              {
                id: 'layer-music-1',
                sceneId: 'scene-1',
                disciplineType: 'music',
                status: 'draft',
                content: {
                  type: 'music',
                  score: {
                    format: 'internal',
                    data: {},
                    measures: [],
                  },
                  tempo: 120,
                  timeSignature: '4/4',
                  key: 'C',
                  dynamics: [],
                  mediaUrl: undefined,
                },
                assignedRoles: [],
                metadata: {},
              },
              {
                id: 'layer-dance-1',
                sceneId: 'scene-1',
                disciplineType: 'dance',
                status: 'draft',
                content: {
                  type: 'dance',
                  choreography: {
                    sequences: [],
                    formations: [],
                    movementPaths: [],
                  },
                  tempo: 120,
                  eightCountStructure: [],
                  mediaUrl: undefined,
                },
                assignedRoles: [],
                metadata: {},
              },
              {
                id: 'layer-drama-1',
                sceneId: 'scene-1',
                disciplineType: 'drama',
                status: 'draft',
                content: {
                  type: 'drama',
                  script: {
                    segments: [],
                    characters: [],
                    blocking: [],
                  },
                  emotionMap: [],
                  mediaUrl: undefined,
                },
                assignedRoles: [],
                metadata: {},
              },
            ],
            cueMarkers: [],
            duration: undefined,
            metadata: {},
          },
        ],
        members: [],
        metadata: {
          description: 'Demo production project',
          targetDate: undefined,
          tags: ['demo'],
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ],
    [mockUser.id]
  );

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    const project = mockProjects.find((p) => p.id === projectId);
    if (project && project.scenes.length > 0) {
      setSelectedSceneId(project.scenes[0].id);
    }
    setActiveView('dashboard');
  };

  const handleCreateProject = () => {
    // In real app, this would open a modal or navigate to creation page
    // Kept as a no-op for now so the control is still visible and wired
    // for future backend integration.
    // eslint-disable-next-line no-console
    console.log('Create new project');
  };

  const handleModeSwitch = (mode: UserMode) => {
    setUserMode(mode);
  };

  const handleSceneChange = (sceneId: string) => {
    setSelectedSceneId(sceneId);
  };

  const handleLayerSelect = (layerId: string) => {
    // eslint-disable-next-line no-console
    console.log('Layer selected:', layerId);
  };

  const handleSceneUpdate = (scene: Scene) => {
    // eslint-disable-next-line no-console
    console.log('Scene updated:', scene);
  };

  const selectedProject = mockProjects.find((p) => p.id === selectedProjectId) ?? mockProjects[0];
  const selectedScene =
    selectedProject?.scenes.find((s) => s.id === selectedSceneId) ?? selectedProject?.scenes[0];

  const effectiveProjectId = selectedProject?.id;

  return (
    <div className="app">
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* ARIA live region for announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="aria-live-region"
        id="aria-announcements"
      />

      {/* Primary navigation so all major features are discoverable */}
      <header className="app-header">
        <div className="app-header-inner">
          <h1 className="app-title">Unified Performing Arts Platform</h1>
          <nav className="app-nav" aria-label="Primary">
            <button
              type="button"
              className={activeView === 'dashboard' ? 'nav-link active' : 'nav-link'}
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </button>
            <button
              type="button"
              className={activeView === 'roles' ? 'nav-link active' : 'nav-link'}
              onClick={() => setActiveView('roles')}
            >
              My Roles
            </button>
            <button
              type="button"
              className={activeView === 'portfolio' ? 'nav-link active' : 'nav-link'}
              onClick={() => setActiveView('portfolio')}
            >
              Portfolio
            </button>
            <button
              type="button"
              className={activeView === 'analytics' ? 'nav-link active' : 'nav-link'}
              onClick={() => setActiveView('analytics')}
              disabled={!effectiveProjectId}
            >
              Analytics
            </button>
            <button
              type="button"
              className={activeView === 'rehearsal' ? 'nav-link active' : 'nav-link'}
              onClick={() => setActiveView('rehearsal')}
            >
              Rehearsal Chat
            </button>
          </nav>

          {/* Mobile-friendly nav hook expected by tests */}
          <nav id="mobile-nav" className="app-nav-mobile" aria-label="Primary mobile navigation">
            <select
              value={activeView}
              onChange={(e) => setActiveView(e.target.value as ActiveView)}
            >
              <option value="dashboard">Dashboard</option>
              <option value="roles">My Roles</option>
              <option value="portfolio">Portfolio</option>
              <option value="analytics" disabled={!effectiveProjectId}>
                Analytics
              </option>
              <option value="rehearsal">Rehearsal Chat</option>
            </select>
          </nav>
        </div>
      </header>

      <main id="main-content">
        {activeView === 'dashboard' && (
          <>
            {!selectedProjectId ? (
              <ProjectDashboard
                userMode={userMode}
                userId={mockUser.id}
                user={mockUser}
                projects={mockProjects}
                onProjectSelect={handleProjectSelect}
                onCreateProject={handleCreateProject}
                onModeSwitch={handleModeSwitch}
              />
            ) : selectedScene && selectedProject ? (
              <div className="container" data-testid="scene-editor">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProjectId(null);
                    setSelectedSceneId(null);
                  }}
                  className="nav-btn"
                  style={{ marginBottom: '1rem' }}
                  aria-label="Back to dashboard"
                >
                  ← Back to Dashboard
                </button>
                <SceneEditor
                  scene={selectedScene}
                  scenes={selectedProject.scenes}
                  userRole={mockUser.role}
                  editMode
                  onSceneChange={handleSceneChange}
                  onLayerSelect={handleLayerSelect}
                  onSceneUpdate={handleSceneUpdate}
                />
              </div>
            ) : null}
          </>
        )}

        {activeView === 'portfolio' && (
          <section aria-label="Student portfolio">
            <PortfolioViewer userId={mockUser.id} />
          </section>
        )}

        {activeView === 'roles' && (
          <section aria-label="Student roles overview">
            <StudentRoleDashboard userId={mockUser.id} />
          </section>
        )}

        {activeView === 'analytics' && effectiveProjectId && (
          <section aria-label="Class analytics">
            <AnalyticsDashboard projectId={effectiveProjectId} teacherId="teacher-1" />
          </section>
        )}

        {activeView === 'rehearsal' && (
          <section aria-label="Rehearsal video chat">
            <RehearsalVideoChat
              rehearsalId={effectiveProjectId ?? 'demo-rehearsal'}
              userId={mockUser.id}
              userName={`${mockUser.profile.firstName} ${mockUser.profile.lastName}`}
              autoConnect={false}
            />
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
